import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true; // Default to dark mode
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#64b5f6' : '#1976d2',
        light: darkMode ? '#90caf9' : '#42a5f5',
        dark: darkMode ? '#1976d2' : '#1565c0',
      },
      secondary: {
        main: darkMode ? '#f48fb1' : '#dc004e',
        light: darkMode ? '#f8bbd9' : '#e91e63',
        dark: darkMode ? '#c2185b' : '#ad1457',
      },
      background: {
        default: darkMode ? '#0a0a0a' : '#fafafa',
        paper: darkMode ? '#1a1a1a' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#ffffff' : '#2c2c2c',
        secondary: darkMode ? '#b0b0b0' : '#666666',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontFamily: '"Inter", sans-serif',
      },
      h2: {
        fontWeight: 700,
        letterSpacing: '-0.01em',
        fontFamily: '"Inter", sans-serif',
      },
      h3: {
        fontWeight: 600,
        fontFamily: '"Inter", sans-serif',
      },
      h4: {
        fontWeight: 600,
        fontFamily: '"Inter", sans-serif',
      },
      h5: {
        fontWeight: 500,
        fontFamily: '"Inter", sans-serif',
      },
      h6: {
        fontWeight: 500,
        fontFamily: '"Inter", sans-serif',
      },
      body1: {
        fontFamily: '"Inter", sans-serif',
        lineHeight: 1.6,
      },
      body2: {
        fontFamily: '"Inter", sans-serif',
        lineHeight: 1.5,
      },
      button: {
        fontFamily: '"Inter", sans-serif',
        fontWeight: 500,
        textTransform: 'none',
      },
      code: {
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: '10px 24px',
            fontSize: '0.95rem',
            fontWeight: 500,
            boxShadow: 'none',
            textTransform: 'none',
            '&:hover': {
              boxShadow: darkMode 
                ? '0 4px 20px rgba(100, 181, 246, 0.3)'
                : '0 4px 20px rgba(25, 118, 210, 0.2)',
            },
          },
          contained: {
            background: darkMode 
              ? 'linear-gradient(135deg, #64b5f6 0%, #42a5f5 100%)'
              : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            '&:hover': {
              background: darkMode
                ? 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)'
                : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: '"Inter", sans-serif',
            '&.code-chip': {
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.75rem',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: darkMode
              ? '0 4px 20px rgba(0,0,0,0.3)'
              : '0 4px 20px rgba(0,0,0,0.08)',
            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            '@media (max-width: 600px)': {
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            '@media (min-width: 601px) and (max-width: 960px)': {
              paddingLeft: '24px',
              paddingRight: '24px',
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: {
            '@media (max-width: 600px)': {
              fontSize: '2rem',
              lineHeight: 1.2,
            },
          },
          h2: {
            '@media (max-width: 600px)': {
              fontSize: '1.75rem',
              lineHeight: 1.3,
            },
          },
          h3: {
            '@media (max-width: 600px)': {
              fontSize: '1.5rem',
              lineHeight: 1.3,
            },
          },
        },
      },
    },
    shape: {
      borderRadius: 12,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
  }), [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};